import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'

export interface GiftCardTenderGate {
  /** true only when settings have loaded AND this tenant has the feature. */
  enabled: boolean
  /** true only while the first fetch is in flight. */
  isLoading: boolean
  /** true when `['settings','all']` failed to load. */
  isError: boolean
}

/**
 * useGiftCardTenderEnabled tells the UI whether a bill may be settled from a
 * gift card balance (F-MM-N24 — backend env `FEATURE_GIFT_CARD_TENDER=1`,
 * surfaced as the server-injected `feature_gift_card_tender` key on
 * GET /settings — env-authoritative, never stored in app_settings).
 *
 *   • enabled  → the payment rail offers "Apply gift card", and the /scan
 *                draw-down screen becomes balance-lookup only
 *   • disabled → the counter shows nothing, and /scan stays the only way a
 *                card is spent (the pre-F-MM-N24 behaviour)
 *
 * Shares the `['settings','all']` query cache with the rest of the app, so
 * this adds no extra network call. A missing key (old backend) reads as
 * disabled — which is the safe direction: the worst case is a cashier being
 * told to use the old screen, not a card being drawn down on a tenant whose
 * backend cannot record it.
 */
export function useGiftCardTenderEnabled(): GiftCardTenderGate {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: Infinity,
  })
  const all = data?.data as Record<string, unknown> | undefined
  return {
    enabled: all?.feature_gift_card_tender === true,
    isLoading,
    isError,
  }
}
